import ModelAbstract from './ModelAbstract';
import { FigureStatus } from '../modules/Enumeration';
import Figure from './Figure';

class FigureDraft extends ModelAbstract {
    constructor({
        id = null,
        name = '',
        companyId = null,
        category = null,
        classification = null,
        origin = null,
        character = null,
        material = null,
        artists = [],
        version = null,
        scale = null,
        height = null,
        width = null,
        length = null,
        unit = null,
        releaseDate = null,
        description = null,
        imageUrl = null,
        rejectionReason = null,
        status = FigureStatus.DRAFT,
        targetFigureId = null,
        targetFigure = null,
        createdBy = null,
        createdByRole = 'user',
        createdAt = null,
        updatedAt = null,
    } = {}) {
        super({ id, createdAt, updatedAt });
        this.name = name;
        this.companyId = companyId;
        this.category = category;
        this.classification = classification;
        this.origin = origin;
        this.character = character;
        this.material = material;
        this.artists = artists || [];
        this.version = version;
        this.scale = scale;
        this.height = height;
        this.width = width;
        this.length = length;
        this.unit = unit;
        this.releaseDate = releaseDate ? new Date(releaseDate) : null;
        this.description = description;
        this.imageUrl = imageUrl;
        this.rejectionReason = rejectionReason;
        this.status = status;
        this.targetFigureId = targetFigureId;
        this.targetFigure = targetFigure;
        this.createdBy = createdBy;
        this.createdByRole = createdByRole;
    }

    /**
     * Factory method to create a FigureDraft instance from API response
     * @param {Object} data - Raw figure draft data from API
     * @returns {FigureDraft} - Instantiated FigureDraft object
     */
    static fromAPI(data) {
        if (!data) return null;

        const targetFigure = data.targetFigure ? Figure.fromAPI(data.targetFigure) : null;
        
        return new FigureDraft({
            id: data.id,
            name: data.name,
            companyId: data.companyId,
            category: data.category,
            classification: data.classification,
            origin: data.origin,
            character: data.character,
            material: data.material,
            artists: data.artists || [],
            version: data.version,
            scale: data.scale,
            height: data.height,
            width: data.width,
            length: data.length,
            unit: data.unit,
            releaseDate: data.releaseDate,
            description: data.description,
            imageUrl: data.imageUrl,
            rejectionReason: data.rejectionReason,
            status: data.status || FigureStatus.DRAFT,
            targetFigureId: data.targetFigure?.id,
            targetFigure: targetFigure,
            createdBy: data.createdBy,
            createdByRole: data.createdByRole,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
        });
    }

    /**
     * Get dimensions as formatted string
     * @returns {string} Formatted dimensions
     */
    getDimensions() {
        const dimensions = [];
        
        if (this.height) dimensions.push(`H: ${this.height}`);
        if (this.width) dimensions.push(`W: ${this.width}`);
        if (this.length) dimensions.push(`L: ${this.length}`);
        
        if (dimensions.length === 0) return '';
        
        return `${dimensions.join(' x ')} ${this.unit || ''}`;
    }

    /**
     * Get status display text
     * @returns {string} Status text
     */
    getStatusText() {
        switch (this.status) {
            case FigureStatus.PENDING:
                return 'Pending Review';
            case FigureStatus.APPROVED:
                return 'Approved';
            case FigureStatus.REJECTED:
                return 'Rejected';
            case FigureStatus.DRAFT:
                return 'Draft';
            default:
                return 'Unknown';
        }
    }

    /**
     * Convert to plain object for JSON serialization
     */
    toJSON() {
        return {
            ...super.toJSON(),
            name: this.name,
            companyId: this.companyId,
            category: this.category,
            classification: this.classification,
            origin: this.origin,
            character: this.character,
            material: this.material,
            artists: this.artists,
            version: this.version,
            scale: this.scale,
            height: this.height,
            width: this.width,
            length: this.length,
            unit: this.unit,
            releaseDate: this.releaseDate?.toISOString() || null,
            description: this.description,
            imageUrl: this.imageUrl,
            rejectionReason: this.rejectionReason,
            status: this.status,
            targetFigureId: this.targetFigureId,
            createdByRole: this.createdByRole,
        };
    }
}

export default FigureDraft; 